/**
 * Achievement List — shows user achievements with progress bars.
 * Achieved items get a glow effect, in-progress show percentage.
 */

import React from 'react'
import { Trophy } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ProgressBar } from '@turnifly/shared/ui'
import type { UserAchievementWithDetails } from '@turnifly/shared/api'

interface AchievementListProps {
  achievements: UserAchievementWithDetails[]
}

export const AchievementList: React.FC<AchievementListProps> = ({ achievements }) => {
  const { t, i18n } = useTranslation()

  if (achievements.length === 0) {
    return (
      <div className="flex flex-col items-center py-6 text-text-hint">
        <Trophy size={28} className="mb-2 opacity-40" />
        <p className="text-sm">{t('profile.noAchievements')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {achievements.map((ua) => {
        const name = i18n.language === 'uz'
          ? ua.achievement.name_uz
          : ua.achievement.name_en
        const isAchieved = ua.status === 'achieved'
        const targetValue = ua.achievement.target_value || 1
        const progressPercent = isAchieved
          ? 100
          : Math.min(Math.round((ua.progress / targetValue) * 100), 100)

        return (
          <div
            key={`${ua.achievement_id}-${ua.year}`}
            className={`
              flex items-center gap-3 p-3 rounded-xl transition-all
              ${isAchieved
                ? 'bg-primary/[0.08] border border-primary/20 shadow-[0_0_12px_rgba(0,97,255,0.08)]'
                : 'bg-white/[0.02] border border-white/[0.04]'
              }
            `}
          >
            {/* Icon */}
            <div className={`
              w-10 h-10 flex items-center justify-center rounded-xl text-lg shrink-0
              ${isAchieved ? 'bg-primary/15' : 'bg-white/[0.04]'}
            `}>
              {ua.achievement.icon}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <span className={`text-sm font-medium truncate ${isAchieved ? 'text-text' : 'text-text/70'}`}>
                  {name}
                </span>
                <span className={`text-xs font-mono shrink-0 ml-2 ${isAchieved ? 'text-primary' : 'text-text-hint'}`}>
                  {isAchieved ? '100%' : `${progressPercent}%`}
                </span>
              </div>

              {/* Progress bar */}
              <div className="mt-1.5">
                <ProgressBar
                  value={progressPercent}
                  variant={isAchieved ? 'success' : 'primary'}
                  height="h-1.5"
                />
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
